/**
 * Convert cue markup (## / **Pose:** / **Say:** / …) into sequence steps.
 * Cue "~N min" labels are class-outline minutes; SequenceStep.holdSeconds are
 * always seconds. Multi-pose blocks split section time evenly, capped at
 * MAX_HOLD_SECONDS so a sparse pose list cannot dump a whole section onto one timer.
 */

import { parseTeachSteps } from './teachSteps';
import {
  defaultHoldForStyle,
  MAX_HOLD_SECONDS,
  normalizeSequence,
  normalizeStep,
} from './sequenceModel';
import { POSE_SHORTCUTS, resolvePoseRef } from './poseImages';

function parseMinutes(timeStr) {
  const m = String(timeStr || '').match(/(\d+)\s*min/i);
  if (m) return Number(m[1]);
  return null;
}

function poseKeyFromImage(img) {
  if (!img) return null;
  // Prefer shortcut keys from alt / known shortcuts.
  const alt = String(img.alt || '');
  for (const [short, name] of Object.entries(POSE_SHORTCUTS)) {
    const display = name.replace(/([a-z])([A-Z])/g, '$1 $2');
    if (alt.toLowerCase().includes(short.toLowerCase())) return short;
    if (alt.includes(name) || alt.includes(display)) return short;
  }
  // resolvePoseRef returns alt like "Mountain (Tadasana)" — try reverse lookup
  const refMatch = alt.match(/^([^(]+)/);
  const guess = (refMatch ? refMatch[1] : alt).trim();
  if (guess && resolvePoseRef(guess)) {
    // Prefer shortcut if one maps to this pose
    for (const [short, name] of Object.entries(POSE_SHORTCUTS)) {
      const resolved = resolvePoseRef(short);
      if (resolved && (resolved.alt === img.alt || resolved.url === img.url)) return short;
    }
    return guess.replace(/\s+/g, '');
  }
  return null;
}

/** Extract raw **Pose:** keys from a cue block body (before image resolution). */
function extractPoseKeysFromBlock(blockText) {
  const keys = [];
  const text = String(blockText || '');
  for (const line of text.split('\n')) {
    const hit = line.match(/^\*\*Pose:?\*\*\s*:?\s*(.+)$/i)
      || line.match(/^Pose\s*:\s*(.+)$/i);
    if (hit) {
      const key = hit[1].trim().replace(/\*+$/, '').trim();
      if (key) keys.push(key);
    }
  }
  return keys;
}

/**
 * @param {string} cues
 * @param {{ style?: string, durationMins?: number }} [opts]
 * @returns {import('./sequenceModel').normalizeStep extends Function ? object[] : object[]}
 */
export function cuesToSequence(cues, { style, durationMins } = {}) {
  const text = String(cues || '').trim();
  if (!text) return [];

  const steps = parseTeachSteps(text);
  if (!steps.length) return [];

  const totalTargetSecs = Number(durationMins) > 0
    ? Number(durationMins) * 60
    : null;

  // Split cue text into blocks matching parseTeachSteps rich blocks.
  const blocks = text.includes('\n---\n')
    ? text.split(/\n---\n/).map((b) => b.trim()).filter(Boolean)
    : text.split(/(?=^##\s+)/m).map((b) => b.trim()).filter(Boolean);

  const out = [];
  const defaultHold = defaultHoldForStyle(style);

  steps.forEach((step, i) => {
    const blockText = blocks[i] || '';
    let poseKeys = extractPoseKeysFromBlock(blockText);

    // Fallback: derive from resolved images on the parsed step.
    if (!poseKeys.length && step.images?.length) {
      poseKeys = step.images
        .map((img) => poseKeyFromImage(img))
        .filter(Boolean);
    }

    // Last resort: skip empty or invent from title via resolve
    if (!poseKeys.length) {
      const fromTitle = resolvePoseRef(step.title);
      if (fromTitle) {
        poseKeys = [step.title.split(/\s+/)[0].toLowerCase()];
        // Prefer a resolving shortcut
        for (const short of Object.keys(POSE_SHORTCUTS)) {
          if (resolvePoseRef(short)?.url === fromTitle.url) {
            poseKeys = [short];
            break;
          }
        }
      }
    }

    if (!poseKeys.length) return;

    const mins = parseMinutes(step.time);
    // Outline minutes → seconds once; never leave minutes in holdSeconds.
    const sectionSecs = mins != null
      ? mins * 60
      : defaultHold * poseKeys.length;

    const perPose = Math.min(
      MAX_HOLD_SECONDS,
      Math.max(8, Math.round(sectionSecs / poseKeys.length)),
    );
    const cue = (step.say || []).join(' ').trim();
    const breath = (step.breath || []).join(' ').trim();
    const tip = (step.tips || []).join(' ').trim();
    const mod = (step.mods || []).join(' ').trim();

    for (const poseKey of poseKeys) {
      // Only keep keys that resolve to an image
      if (!resolvePoseRef(poseKey)) continue;
      const normalized = normalizeStep(
        {
          poseKey,
          holdSeconds: perPose,
          cue,
          breath,
          tip: tip || undefined,
          mod: mod || undefined,
        },
        { style },
      );
      if (normalized) out.push(normalized);
    }
  });

  let seq = normalizeSequence(out, { style });

  // Only shrink holds if they overrun the class length. Never inflate — that
  // turned sparse **Pose:** lists into ~17 minute single-pose timers.
  if (totalTargetSecs && seq.length) {
    const sum = seq.reduce((s, x) => s + x.holdSeconds, 0);
    if (sum > totalTargetSecs * 1.12) {
      const factor = totalTargetSecs / sum;
      seq = seq.map((s) => ({
        ...s,
        holdSeconds: Math.min(
          MAX_HOLD_SECONDS,
          Math.max(8, Math.round(s.holdSeconds * factor)),
        ),
      }));
    }
  }

  return seq;
}

/** True when every poseKey resolves via poseImages. */
export function sequencePosesResolve(steps) {
  return normalizeSequence(steps).every((s) => !!resolvePoseRef(s.poseKey));
}
